import { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PublishIcon from "@mui/icons-material/Publish";
import ArchiveIcon from "@mui/icons-material/Archive";

import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import Buttons from "../../../components/Buttons/Buttons";
import { getSurveyStatus, isAssignable, STATUS_CHIP_CONFIG, SURVEY_STATUS } from "../../../utils/surveysConstants";

const SURVEYS_TABLE_COLUMN_COUNT = 7;

const SurveysListRows = ({
  rows,
  page,
  rowsPerPage,
  totalRows,
  onChangePage,
  onChangeRowsPerPage,
  isLoading,
  fetchError,
  onRetry,
  onEdit,
  onView,
  onViewResults,
  onPublish,
  onArchive,
  onAssign,
  onExportJSON,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const status = selectedRow ? getSurveyStatus(selectedRow) : "";

  return (
    <div>
      <div className="table-container survey-table-container">
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>SL NO</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell style={{ minWidth: "220px", whiteSpace: "nowrap" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="survey-table-body">
              {rows?.length === 0 ? (
                <TableStatusRow
                  colSpan={SURVEYS_TABLE_COLUMN_COUNT}
                  loading={isLoading}
                  error={fetchError}
                  onRetry={onRetry}
                  emptyMessage="No surveys found"
                />
              ) : (
                rows.map((row, index) => {
                  const rowStatus = getSurveyStatus(row);
                  const chip = STATUS_CHIP_CONFIG[rowStatus] || { label: rowStatus, color: "default" };
                  return (
                    <TableRow
                      key={row.id}
                      onClick={(e) => {
                        if (e.target.closest('.survey-actions-cell') || e.target.closest('button')) {
                          return;
                        }
                        onViewResults(row);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell className="survey-sl-cell">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell className="survey-title-cell">{row.title}</TableCell>
                      <TableCell className="survey-status-cell">
                        <Chip label={chip.label} color={chip.color} size="small" />
                      </TableCell>
                      <TableCell className="survey-version-cell">{row.version ?? "-"}</TableCell>
                      <TableCell className="survey-published-cell">{row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : "-"}</TableCell>
                      <TableCell className="survey-updated-cell">{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "-"}</TableCell>
                      <TableCell className="survey-actions-cell" style={{ whiteSpace: "nowrap" }}>
                        <div className="survey-actions-wrapper" style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                          <Buttons
                            text="View"
                            type="button"
                            variant="smallsecondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView && onView(row);
                            }}
                          />
                          <Buttons
                            text="Results"
                            type="button"
                            variant="smallsecondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewResults(row);
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, row)}
                            sx={{
                              color: "#64748b",
                              border: "1px solid #cbd5e1",
                              borderRadius: "6px",
                              padding: "4px",
                              "&:hover": { backgroundColor: "#f1f5f9", color: "#0f172a" },
                            }}
                            title="More Actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* COMPACT ACTIONS OVERFLOW MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 160,
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            handleMenuClose(e);
            if (selectedRow) onEdit(selectedRow);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" style={{ color: "#104581" }} />
          </ListItemIcon>
          <ListItemText primary="Edit Survey" />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            handleMenuClose(e);
            if (selectedRow) onExportJSON && onExportJSON(selectedRow);
          }}
        >
          <ListItemIcon>
            <CodeIcon fontSize="small" style={{ color: "#d97706" }} />
          </ListItemIcon>
          <ListItemText primary="Export JSON" sx={{ color: "#b45309", fontWeight: 600 }} />
        </MenuItem>

        {selectedRow && isAssignable(selectedRow) && (
          <MenuItem
            onClick={(e) => {
              handleMenuClose(e);
              onAssign(selectedRow);
            }}
          >
            <ListItemIcon>
              <PersonAddIcon fontSize="small" style={{ color: "#279191" }} />
            </ListItemIcon>
            <ListItemText primary="Assign to Patient" />
          </MenuItem>
        )}

        {status === SURVEY_STATUS.DRAFT && (
          <MenuItem
            onClick={(e) => {
              handleMenuClose(e);
              if (selectedRow) onPublish(selectedRow);
            }}
          >
            <ListItemIcon>
              <PublishIcon fontSize="small" style={{ color: "#10b981" }} />
            </ListItemIcon>
            <ListItemText primary="Publish Survey" />
          </MenuItem>
        )}

        {status !== SURVEY_STATUS.ARCHIVED && (
          <MenuItem
            onClick={(e) => {
              handleMenuClose(e);
              if (selectedRow) onArchive(selectedRow);
            }}
            sx={{ color: "#ef4444" }}
          >
            <ListItemIcon>
              <ArchiveIcon fontSize="small" style={{ color: "#ef4444" }} />
            </ListItemIcon>
            <ListItemText primary="Archive" />
          </MenuItem>
        )}
      </Menu>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </div>
  );
};

export default SurveysListRows;
